import { LexError } from './lex.js';
import type { TextError } from './source.js';

export type PyTokenKind =
  | 'ident'
  | 'number'
  | 'string'
  | 'fstring'
  | 'op'
  /** End of a logical line; not emitted for blank lines, comment lines or inside brackets. */
  | 'newline'
  | 'indent'
  | 'dedent'
  | 'eof';

/** A piece of an f-string: literal text, or a `{…}` hole already lexed into its own tokens. */
export type FPart = { kind: 'text'; value: string } | { kind: 'code'; tokens: PyToken[] };

export interface PyToken {
  kind: PyTokenKind;
  /** The text as written; for `string`, the decoded value. */
  text: string;
  line: number;
  column: number;
  from: number;
  to: number;
  /** Only on an `fstring`: its literal pieces and holes, left to right. */
  parts?: FPart[];
}

/**
 * Longest first: `//=` must win over `//`, which must win over `/`. `**` is lexed (so the parser
 * can name it in an "unsupported" message) even though there is no power in the IR.
 */
const OPS = [
  '//=',
  '**=',
  '//',
  '**',
  '==',
  '!=',
  '<=',
  '>=',
  '+=',
  '-=',
  '*=',
  '/=',
  '%=',
  '+',
  '-',
  '*',
  '/',
  '%',
  '<',
  '>',
  '=',
  '(',
  ')',
  '[',
  ']',
  '{',
  '}',
  ',',
  '.',
  ':',
  ';',
];

const ID_START = /[A-Za-z_]/;
const ID_PART = /[A-Za-z0-9_]/;
const DIGIT = /[0-9]/;
const OPENERS = '([{';
const CLOSERS = ')]}';

function decodeEscape(c: string | undefined): string {
  switch (c) {
    case 'n':
      return '\n';
    case 't':
      return '\t';
    case 'r':
      return '\r';
    case '\\':
      return '\\';
    case '"':
      return '"';
    case "'":
      return "'";
    default:
      return c ?? '';
  }
}

/**
 * Turns Python text into tokens, with INDENT/DEDENT for the block structure. Comments are
 * skipped; so are blank and comment-only lines, which carry no indentation of their own.
 *
 * Indentation is compared as *text*, not as a width: a deeper line must start with the whole of
 * the line it nests under. That is what makes mixing tabs and spaces an error the student can act
 * on rather than a program that silently means something else.
 */
export function lexPy(source: string): PyToken[] {
  const tokens: PyToken[] = [];
  let i = 0;
  let line = 1;
  let lineStart = 0;
  /** Depth of (), [] and {}: inside them a newline continues the line and indentation is free. */
  let bracket = 0;
  let atLineStart = true;
  let lineHasTokens = false;
  const indents: string[] = [''];

  const col = () => i - lineStart + 1;

  // Annotated, not inferred: only a declared `never` return lets the compiler see that the code
  // after a `fail(…)` is unreachable.
  const fail: (
    code: TextError['code'],
    from: number,
    to: number,
    at: { line: number; column: number },
    params?: Record<string, string>,
  ) => never = (code, from, to, at, params) => {
    throw new LexError({
      code,
      line: at.line,
      column: at.column,
      span: { from, to: Math.max(to, from + 1) },
      ...(params ? { params } : {}),
    });
  };

  const add = (kind: PyTokenKind, text: string, from: number, to: number, column: number) => {
    tokens.push({ kind, text, line, column, from, to });
    if (kind !== 'indent' && kind !== 'dedent') lineHasTokens = true;
  };

  /** INDENT/DEDENT for a content line whose leading whitespace is `ws`. */
  const layout = (ws: string, at: number) => {
    const top = indents[indents.length - 1]!;
    const column = at - lineStart + 1;
    if (ws === top) return;
    if (ws.length > top.length) {
      if (!ws.startsWith(top)) {
        fail('expected', at, at + 1, { line, column }, { expected: 'matching indentation' });
      }
      indents.push(ws);
      tokens.push({ kind: 'indent', text: ws, line, column, from: at, to: at });
      return;
    }
    while (indents.length > 1 && indents[indents.length - 1]!.length > ws.length) {
      indents.pop();
      tokens.push({ kind: 'dedent', text: ws, line, column, from: at, to: at });
    }
    if (indents[indents.length - 1] !== ws) {
      fail('expected', at, at + 1, { line, column }, { expected: 'matching indentation' });
    }
  };

  /** The tokens of one `{…}` hole of an f-string, positioned as they sit in the whole source. */
  const hole = (from: number, to: number): PyToken[] => {
    let start = from;
    while (start < to && (source[start] === ' ' || source[start] === '\t')) start++;
    const shiftCol = start - lineStart;
    const inner = lexPy(source.slice(start, to));
    return (
      inner
        // A hole is one line of code: its own layout and line end carry no meaning out here.
        .filter((t) => t.kind !== 'indent' && t.kind !== 'dedent' && t.kind !== 'newline')
        .map((t) => ({
          ...t,
          line,
          column: t.column + shiftCol,
          from: t.from + start,
          to: t.to + start,
        }))
    );
  };

  /** An f-string, from the `f`; returns the token. */
  const fstring = (from: number, startCol: number): PyToken => {
    const quote = source[i + 1]!;
    i += 2;
    const parts: FPart[] = [];
    let text = '';
    const flush = () => {
      if (text !== '') parts.push({ kind: 'text', value: text });
      text = '';
    };
    while (i < source.length && source[i] !== quote) {
      const c = source[i]!;
      if (c === '\n') fail('unterminated', from, i, { line, column: startCol }, { what: 'text' });
      if ((c === '{' && source[i + 1] === '{') || (c === '}' && source[i + 1] === '}')) {
        text += c;
        i += 2;
        continue;
      }
      if (c === '{') {
        flush();
        i++;
        const open = i;
        let depth = 0;
        while (i < source.length) {
          const ch = source[i]!;
          if (ch === '\n')
            fail('unterminated', from, i, { line, column: startCol }, { what: 'text' });
          if (OPENERS.includes(ch)) depth++;
          else if (ch === '}' && depth === 0) break;
          else if (CLOSERS.includes(ch)) depth--;
          else if ((ch === ':' || ch === '!') && depth === 0) {
            fail('unsupported', i, i + 1, { line, column: i - lineStart + 1 }, { what: `${ch}…` });
          }
          i++;
        }
        if (source[i] !== '}') {
          fail('unterminated', from, i, { line, column: startCol }, { what: 'text' });
        }
        parts.push({ kind: 'code', tokens: hole(open, i) });
        i++;
        continue;
      }
      if (c === '\\') {
        text += decodeEscape(source[i + 1]);
        i += 2;
        continue;
      }
      text += c;
      i++;
    }
    if (i >= source.length) {
      fail('unterminated', from, i, { line, column: startCol }, { what: 'text' });
    }
    i++;
    flush();
    lineHasTokens = true;
    return {
      kind: 'fstring',
      text: '',
      line,
      column: startCol,
      from,
      to: i,
      parts,
    };
  };

  while (i < source.length) {
    if (atLineStart) {
      atLineStart = false;
      if (bracket === 0) {
        let j = i;
        while (j < source.length && (source[j] === ' ' || source[j] === '\t')) j++;
        const c = source[j];
        if (c === undefined || c === '\n' || c === '\r' || c === '#') {
          // A blank or comment-only line has no indentation of its own: skip the whole line.
          i = j;
          while (i < source.length && source[i] !== '\n') i++;
          continue;
        }
        layout(source.slice(i, j), j);
        i = j;
      }
    }
    const c = source[i]!;
    if (c === '\n') {
      if (bracket === 0 && lineHasTokens) {
        tokens.push({ kind: 'newline', text: '\n', line, column: col(), from: i, to: i + 1 });
        lineHasTokens = false;
      }
      i++;
      line++;
      lineStart = i;
      atLineStart = true;
      continue;
    }
    if (c === ' ' || c === '\t' || c === '\r') {
      i++;
      continue;
    }
    if (c === '#') {
      while (i < source.length && source[i] !== '\n') i++;
      continue;
    }
    // A backslash at the end of a line joins it to the next one, indentation and all.
    if (
      c === '\\' &&
      (source[i + 1] === '\n' || (source[i + 1] === '\r' && source[i + 2] === '\n'))
    ) {
      i += source[i + 1] === '\r' ? 3 : 2;
      line++;
      lineStart = i;
      continue;
    }
    const from = i;
    const startCol = col();
    if (DIGIT.test(c) || (c === '.' && DIGIT.test(source[i + 1] ?? ''))) {
      while (i < source.length && DIGIT.test(source[i]!)) i++;
      if (source[i] === '.' && DIGIT.test(source[i + 1] ?? '')) {
        i++;
        while (i < source.length && DIGIT.test(source[i]!)) i++;
      }
      add('number', source.slice(from, i), from, i, startCol);
      continue;
    }
    if ((c === 'f' || c === 'F') && (source[i + 1] === '"' || source[i + 1] === "'")) {
      tokens.push(fstring(from, startCol));
      continue;
    }
    if (ID_START.test(c)) {
      while (i < source.length && ID_PART.test(source[i]!)) i++;
      add('ident', source.slice(from, i), from, i, startCol);
      continue;
    }
    if (c === '"' || c === "'") {
      const quote = c;
      i++;
      let value = '';
      while (i < source.length && source[i] !== quote) {
        if (source[i] === '\n') {
          fail('unterminated', from, i, { line, column: startCol }, { what: 'text' });
        }
        if (source[i] === '\\') {
          value += decodeEscape(source[i + 1]);
          i += 2;
          continue;
        }
        value += source[i];
        i++;
      }
      if (i >= source.length) {
        fail('unterminated', from, i, { line, column: startCol }, { what: 'text' });
      }
      i++;
      add('string', value, from, i, startCol);
      continue;
    }
    const op = OPS.find((o) => source.startsWith(o, i));
    if (!op) {
      i++;
      fail('expected', from, i, { line, column: startCol }, { found: c });
    }
    if (OPENERS.includes(op)) bracket++;
    else if (CLOSERS.includes(op) && bracket > 0) bracket--;
    i += op.length;
    add('op', op, from, i, startCol);
  }

  if (lineHasTokens) {
    tokens.push({ kind: 'newline', text: '\n', line, column: col(), from: i, to: i });
  }
  while (indents.length > 1) {
    indents.pop();
    tokens.push({ kind: 'dedent', text: '', line, column: col(), from: i, to: i });
  }
  tokens.push({
    kind: 'eof',
    text: '',
    line,
    column: col(),
    from: source.length,
    to: source.length,
  });
  return tokens;
}
